import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  AspectRatio,
  Modal,
  ModalDialog,
  IconButton,
  Chip,
} from '@mui/joy';
import { Camera, UploadSimple as Upload, X, MagnifyingGlassPlus as ZoomIn } from '@phosphor-icons/react';

interface Photo {
  id: number;
  image: string;
  thumbnail?: string;
  description?: string;
  created_at: string;
}

interface ChecklistStepPhotosProps {
  stepRunId: number;
  photosRequired: boolean;
  photos: Photo[];
  loading?: boolean;
  readOnly?: boolean;
  error?: string | null;
  onUploadClick: () => void;
}

export const ChecklistStepPhotos: React.FC<ChecklistStepPhotosProps> = ({
  photosRequired,
  photos,
  loading = false,
  readOnly = false,
  error = null,
  onUploadClick,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);


  const handlePhotoClick = (photo: Photo) => {
    setSelectedPhoto(photo);
  };

  const closePhotoModal = () => {
    setSelectedPhoto(null);
  };

  return (
    <Box>
      {/* Upload Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          startDecorator={<Upload size={16} />}
          disabled={readOnly}
          onClick={onUploadClick}
          size="sm"
        >
          {photos.length > 0 ? 'Add More Photos' : 'Upload Photos'}
        </Button>
        
        {photosRequired && (
          <Chip 
            size="sm" 
            color={photos.length > 0 ? 'success' : 'warning'}
            variant="soft"
          >
            {photos.length > 0 ? `✓ ${photos.length} photos uploaded` : '📸 Photos required'}
          </Chip>
        )}
      </Box>

      {/* Error Display */}
      {error && (
        <Typography level="body-sm" color="danger" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Loading State */}
      {loading && (
        <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
          Loading photos...
        </Typography>
      )}

      {/* Photos Grid */}
      {photos.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1, mb: 2 }}>
          {photos.map((photo) => (
            <Card
              key={photo.id}
              variant="outlined"
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 'sm',
                },
              }}
              role="button"
              tabIndex={0}
              aria-label={photo.description || 'Enlarge inspection photo'}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePhotoClick(photo); } }}
              onClick={() => handlePhotoClick(photo)}
            >
              <AspectRatio ratio="1">
                <img
                  src={photo.thumbnail || photo.image}
                  alt={photo.description || 'Checklist step photo'}
                  style={{ 
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
              </AspectRatio>
              <Box sx={{ 
                position: 'absolute', 
                top: 4, 
                right: 4,
                bgcolor: 'rgba(0,0,0,0.6)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <ZoomIn size={12} color="white" />
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {/* No Photos State */}
      {!loading && photos.length === 0 && !photosRequired && (
        <Box sx={{ 
          textAlign: 'center', 
          p: 3, 
          border: '1px dashed', 
          borderColor: 'neutral.300',
          borderRadius: 'sm',
          bgcolor: 'background.level1'
        }}>
          <Camera size={32} style={{ opacity: 0.5, marginBottom: 8 }} />
          <Typography level="body-sm" color="neutral">
            No photos uploaded for this step
          </Typography>
        </Box>
      )}

      {/* Photo Modal */}
      <Modal open={!!selectedPhoto} onClose={closePhotoModal}>
        <ModalDialog sx={{ maxWidth: '90vw', maxHeight: '90vh', p: 2 }}>
          {selectedPhoto && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography level="h4">Photo</Typography>
                <IconButton variant="plain" color="neutral" aria-label="Close photo" onClick={closePhotoModal}>
                  <X />
                </IconButton>
              </Box>
              
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={selectedPhoto.image}
                  alt={selectedPhoto.description || 'Checklist step photo'}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
              </Box>
              
              {selectedPhoto.description && (
                <Typography level="body-sm" sx={{ mt: 2, textAlign: 'center' }}>
                  {selectedPhoto.description}
                </Typography>
              )}
              
              <Typography level="body-xs" color="neutral" sx={{ mt: 1, textAlign: 'center' }}>
                Uploaded: {new Date(selectedPhoto.created_at).toLocaleString()}
              </Typography>
            </>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};